import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// Drives the Stocktake screen — physical-count workflow.
//
// Data:
//   activeItems       — items the manager is counting (active rows only)
//   openStocktake     — the in-progress stocktake (completed_at IS NULL)
//                       and its stocktake_items, keyed by item_id
//   history           — recently-completed stocktakes (last 10)
//
// Mutators:
//   startStocktake()                  — creates a new stocktakes row,
//                                       returns the row
//   upsertStocktakeItem({ item_id,
//     counted_qty, expected_qty })    — upsert one count, partial save
//                                       leaves the stocktake open
//   completeStocktake(id)             — calls complete_stocktake RPC,
//                                       which snapshots + updates items
//   discardStocktake(id)              — deletes the open stocktake +
//                                       its items
//
// All mutations re-fetch on success so the screen stays consistent.
//
// Concurrency note: Phase 1 assumes one open stocktake per restaurant.
// If two managers start one simultaneously, the second sees the first's
// open stocktake on next refresh — they can resume or discard.
export function useStocktakeData() {
  const [activeItems, setActiveItems]   = useState([])
  const [openStocktake, setOpenStocktake] = useState(null)
  const [openItems, setOpenItems]       = useState([])  // stocktake_items for openStocktake
  const [history, setHistory]           = useState([])
  const [loading, setLoading]           = useState(true)
  const [error, setError]               = useState(null)

  // Multi-unit scope — null for single-unit (queries byte-identical).
  const { currentRestaurantId } = useCurrentUnit()

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Apply the unit filter only when grouped; single-unit (null) is
    // untouched and keeps relying on RLS membership scoping. stocktake_items
    // is NOT scoped here — it's already constrained via the stocktake_id FK.
    const scope = (q) => (currentRestaurantId ? q.eq('restaurant_id', currentRestaurantId) : q)
    try {
      // 1. Active items (the things to count). Pull pack info for the
      //    inner-unit display, plus cost so we can show $-impact.
      const { data: itemsData, error: itemsErr } = await scope(supabase
        .from('items')
        .select(`
          id, name, category, unit, cost_per_unit, current_on_hand,
          pack_size, pack_unit, last_stocktake_at, active
        `)
        .eq('active', true)
        .order('name', { ascending: true }))
      if (itemsErr) throw itemsErr
      setActiveItems(itemsData ?? [])

      // 2. Most-recent open stocktake (completed_at IS NULL), if any.
      const { data: openData, error: openErr } = await scope(supabase
        .from('stocktakes')
        .select('id, started_at, completed_at, notes, user_id')
        .is('completed_at', null)
        .order('started_at', { ascending: false })
        .limit(1))
        .maybeSingle()
      if (openErr) throw openErr
      setOpenStocktake(openData ?? null)

      if (openData?.id) {
        const { data: items, error: itemsErr2 } = await supabase
          .from('stocktake_items')
          .select('id, item_id, counted_qty, expected_qty, notes, counted_at')
          .eq('stocktake_id', openData.id)
        if (itemsErr2) throw itemsErr2
        setOpenItems(items ?? [])
      } else {
        setOpenItems([])
      }

      // 3. History — last 10 completed stocktakes with item counts.
      //    The aggregated count + total_variance_qty come from a
      //    server-computed aggregate so the list is cheap.
      const { data: histData, error: histErr } = await scope(supabase
        .from('stocktakes')
        .select(`
          id, started_at, completed_at, notes,
          items:stocktake_items(counted_qty, expected_qty)
        `)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(10))
      if (histErr) throw histErr
      setHistory(histData ?? [])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useStocktakeData]', e)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [currentRestaurantId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Lookup map for the screen — open stocktake item by item_id.
  const openItemsById = useMemo(() => {
    const m = new Map()
    for (const it of openItems) m.set(it.item_id, it)
    return m
  }, [openItems])

  // ---------- Start a new stocktake -------------------------
  // Creates a stocktakes row + records who started it. Returns
  // the new row so the screen can transition into the count UI.
  const startStocktake = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser()
    // For single-unit (null) we omit restaurant_id and let the 0026
    // default trigger fill it — byte-identical to before. For a grouped
    // user we set it explicitly so the count files under the SELECTED unit
    // rather than the trigger's first-membership default.
    const { data, error: insErr } = await supabase
      .from('stocktakes')
      .insert({
        user_id: user?.id ?? null,
        ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      })
      .select()
      .single()
    if (insErr) throw insErr
    await fetchAll()
    return data
  }, [fetchAll, currentRestaurantId])

  // ---------- Upsert one count ------------------------------
  // Called as the manager types each count. Idempotent via the
  // unique (stocktake_id, item_id) constraint — first call inserts,
  // subsequent calls update.
  const upsertStocktakeItem = useCallback(async ({
    stocktake_id, item_id, counted_qty, expected_qty, notes,
  }) => {
    if (!stocktake_id || !item_id) {
      throw new Error('stocktake_id and item_id are required')
    }
    const payload = {
      stocktake_id,
      item_id,
      counted_qty: Number(counted_qty) || 0,
      expected_qty: Number(expected_qty) || 0,
      notes: notes?.trim() || null,
      counted_at: new Date().toISOString(),
    }
    const { error: upErr } = await supabase
      .from('stocktake_items')
      .upsert(payload, { onConflict: 'stocktake_id,item_id' })
    if (upErr) throw upErr
    // Don't fetchAll here — that would re-render every keystroke.
    // The screen calls refresh() after it's done with a batch.
  }, [])

  // ---------- Complete --------------------------------------
  // Atomically writes snapshots + updates items.current_on_hand
  // via the complete_stocktake RPC. Returns { item_count,
  // total_variance_qty } for the toast.
  const completeStocktake = useCallback(async (stocktake_id) => {
    if (!stocktake_id) throw new Error('stocktake_id is required')
    const { data, error: rpcErr } = await supabase
      .rpc('complete_stocktake', { p_stocktake_id: stocktake_id })
    if (rpcErr) throw rpcErr
    await fetchAll()
    // RPC returns a single-row result table
    const summary = Array.isArray(data) ? data[0] : data
    return {
      item_count:        Number(summary?.item_count        || 0),
      total_variance_qty: Number(summary?.total_variance_qty || 0),
    }
  }, [fetchAll])

  // ---------- Discard ---------------------------------------
  // Cascades: the FK on stocktake_items.stocktake_id has ON DELETE
  // CASCADE, so just deleting the parent row clears the in-progress
  // counts.
  const discardStocktake = useCallback(async (stocktake_id) => {
    if (!stocktake_id) throw new Error('stocktake_id is required')
    const { error: delErr } = await supabase
      .from('stocktakes')
      .delete()
      .eq('id', stocktake_id)
    if (delErr) throw delErr
    await fetchAll()
  }, [fetchAll])

  return {
    activeItems,
    openStocktake,
    openItems,
    openItemsById,
    history,
    loading,
    error,
    refresh: fetchAll,
    startStocktake,
    upsertStocktakeItem,
    completeStocktake,
    discardStocktake,
  }
}
